import { useCallback } from "react";
import {
  buildMaxTimeline,
  type Exercise,
  type ReportSectionId,
  type TimedSessionResult,
  type TimeRange,
} from "@heraion/shared";

import type { MaxHistory } from "./ProgressPrTimelineCard";
import type { ReportPageNumber } from "./ReportPageHeader";
import {
  panelE1RMRanking,
  panelMovementBalance,
  trendCellPatterns,
  type RowSlice,
} from "./reportSectionRows";

// PG-64, inbox 978a9aae: «Share as image» shares a series of 4:5 pages, one
// section each, a long list over several. Instagram's carousel holds 10.
export const MAX_REPORT_PAGES = 10;

// Rows of a list section that fit one page, read on the bench at 1080×1350.
const ROWS_PER_PAGE: Partial<Record<ReportSectionId, number>> = {
  e1rmTrends: 3,
  movementBalance: 10,
  prTimeline: 5,
};
// Rows the truncation note takes the place of, on the page that carries it:
// a card is never cut at the foot of a page.
const NOTE_ROWS: Partial<Record<ReportSectionId, number>> = {
  e1rmTrends: 1,
  movementBalance: 2,
  prTimeline: 1,
};

export type ReportPagePlan = { id: ReportSectionId; slice?: RowSlice };

/** One page of the running series: what it draws and where it sits. */
export type ReportPageRun = {
  plan: ReportPagePlan;
  page: ReportPageNumber;
  /** Last page of a series cut at MAX_REPORT_PAGES: it says so. */
  truncated: boolean;
};

/** The pages of `order`, in order: a section per page, a list cut in pages of
 *  ROWS_PER_PAGE rows. Past MAX_REPORT_PAGES the series stops, and its last
 *  page gives up NOTE_ROWS rows to the note that says so. */
export function planReportPages(
  order: readonly ReportSectionId[],
  rowCount: (id: ReportSectionId) => number,
): ReportPageRun[] {
  const plans = order.flatMap<ReportPagePlan>((id) => {
    const perPage = ROWS_PER_PAGE[id];
    const n = perPage === undefined ? 0 : rowCount(id);
    if (perPage === undefined || n <= perPage) return [{ id }];
    const pages: ReportPagePlan[] = [];
    for (let start = 0; start < n; start += perPage) {
      pages.push({ id, slice: [start, Math.min(start + perPage, n)] });
    }
    return pages;
  });
  const truncated = plans.length > MAX_REPORT_PAGES;
  const kept = plans.slice(0, MAX_REPORT_PAGES);
  const last = kept[kept.length - 1];
  if (truncated && last) {
    // A section with no rows to give (Rhythm) has room for it as it is.
    const room = (ROWS_PER_PAGE[last.id] ?? 0) - (NOTE_ROWS[last.id] ?? 0);
    if (room > 0) {
      const start = last.slice?.[0] ?? 0;
      const end = last.slice?.[1] ?? rowCount(last.id);
      kept[kept.length - 1] = { id: last.id, slice: [start, Math.min(end, start + room)] };
    }
  }
  return kept.map((plan, index) => ({
    plan,
    page: { index, total: kept.length },
    truncated: truncated && index === kept.length - 1,
  }));
}

/** The plan over the rows the report holds now. A callback, so the rows are
 *  counted when he shares, not on every change of the open report. */
export function useReportPagePlan(
  {
    results,
    catalog,
    range,
  }: {
    results: TimedSessionResult[] | null;
    catalog: ReadonlyArray<Exercise> | null;
    range: TimeRange;
  },
  order: readonly ReportSectionId[],
  maxHistory: MaxHistory,
): () => ReportPageRun[] {
  const maxItems = maxHistory.items;
  return useCallback(() => {
    const rowCount = (id: ReportSectionId): number => {
      if (id === "prTimeline") {
        return maxItems ? buildMaxTimeline(maxItems, range).rows.length : 0;
      }
      if (!results || !catalog) return 0;
      if (id === "e1rmTrends") {
        return trendCellPatterns(panelE1RMRanking({ results, catalog, range })).length;
      }
      if (id === "movementBalance") {
        return panelMovementBalance({ results, catalog, range }).length;
      }
      return 0;
    };
    return planReportPages(order, rowCount);
  }, [order, results, catalog, range, maxItems]);
}
